// MaterialListTable.tsx — Presentational table for per-panel materials.
// Extracted from MaterialsListDialog so the import-preview path (CV-180) can
// reuse the same row layout without duplicating column definitions.

import { calcMaterials, type MaterialsResult } from '../utils/materialsCalc';
import { formatImperial } from '../utils/imperial';
import type { PanelData } from '../types/panelvar';

export interface MaterialListTableProps {
  panels: PanelData[];
  isMetric: boolean;
  /** Optional highlight — the row matching this panel id gets `.active-row`. */
  activePanelId?: string | null;
}

export default function MaterialListTable({ panels, isMetric, activePanelId }: MaterialListTableProps) {
  const results: MaterialsResult[] = panels.map(p => calcMaterials(p));

  const fmtVol  = (r: MaterialsResult) => isMetric ? `${r.concreteCuM.toFixed(3)} m³`     : `${r.concreteCuYd.toFixed(3)} yd³`;
  const fmtWt   = (r: MaterialsResult) => isMetric ? `${Math.round(r.panelWeightKg).toLocaleString()} kg` : `${r.panelWeightKips.toFixed(1)} kips`;
  const fmtArea = (sqFt: number, sqM: number) => isMetric ? `${sqM.toFixed(1)} m²` : `${sqFt.toFixed(1)} sf`;
  const fmtLoad = (lb: number) => {
    if (lb === 0) return '—';
    return isMetric ? `${Math.round(lb * 0.453592).toLocaleString()} kg` : `${(lb / 1000).toFixed(1)} k`;
  };
  const fmtCg = (inches: number) => isMetric ? `${Math.round(inches * 25.4)} mm` : formatImperial(inches);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="cv-table" style={{ fontSize: 11 }}>
        <thead>
          <tr>
            <th>Mark</th>
            <th>Vol</th>
            <th>Weight</th>
            <th>CG X</th>
            <th>CG Y</th>
            <th>Fin. Area</th>
            <th>H/T</th>
            <th>Ld/Pick</th>
            <th>Ld/Brace</th>
            <th>Br</th>
            <th>Pk</th>
            <th>Wc</th>
          </tr>
        </thead>
        <tbody>
          {panels.map((p, i) => {
            const r = results[i];
            return (
              <tr key={p.id || r.mark || i} className={p.id === activePanelId ? 'active-row' : ''}>
                <td>{r.mark}</td>
                <td>{fmtVol(r)}</td>
                <td>{fmtWt(r)}</td>
                <td>{fmtCg(r.cgX)}</td>
                <td>{fmtCg(r.cgY)}</td>
                <td>{fmtArea(r.finishedAreaSqFt, r.finishedAreaSqM)}</td>
                <td>{r.slendernessRatio.toFixed(1)}</td>
                <td>{fmtLoad(r.craneLoadPerPickLb)}</td>
                <td>{fmtLoad(r.braceLoadPerPointLb)}</td>
                <td>{r.braceCount}</td>
                <td>{r.pickCount}</td>
                <td>{r.weldCount}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
